/**
 * Sends a request to the parent window via postMessage and waits for a response.
 * Intended for use inside an iframe. Rejects if no response is received within the timeout.
 *
 * @param {string} type - Request type identifier
 * @param {object} payload - Additional data to include in the message
 * @param {number} [timeout=5000] - Time in ms to wait for a response before rejecting
 * @returns {Promise<object>} Resolves with the response data from the parent
 */

export default function requestFromParent(type, payload, timeout = 5000) {
  return new Promise((resolve, reject) => {
    const requestId = crypto.randomUUID();

    const timer = setTimeout(() => {
      window.removeEventListener('message', handler);
      reject(new Error(`Request timeout: ${type}`));
    }, timeout);

    function handler(event) {
      if (event.source !== window.parent) return;
      if (event.data?.requestId !== requestId) return;

      clearTimeout(timer);
      window.removeEventListener('message', handler);
      resolve(event.data);
    }

    window.addEventListener('message', handler);
    window.parent.postMessage({ ...payload, type, requestId }, '*');
  });
}
